import _ from 'underscore';

import ChangeScopeTypes from 'bundles/author-course/constants/ChangeScopeTypes';
import TrackIds from 'bundles/author-course/constants/TrackIds';
import { canModifyAndDeleteModule, humanizeTime } from 'bundles/author-course/utils/CourseMaterialUtils';
import { areValidLessons } from 'bundles/author-course/utils/CourseMaterialValidationUtils';

import type { ParentIdsForItemId } from './Lesson';
import Lesson from './Lesson';

// TODO: Typing for this model is not finished.
class Module {
  declare id: string;

  declare name: string;

  declare description: string;

  declare timeCommitment: any;

  declare learningObjectives: Array<any>;

  declare elements: Array<any>;

  declare context: any;

  declare isUpdated: boolean;

  declare lessons: Array<Lesson>;

  // TODO:
  declare lessonOptions: any;

  constructor({
    id,
    name,
    description,
    timeCommitment,
    learningObjectives,
    elements,
    drafts,
    context,
    authoringAtoms,
    authoringAtomRelations,
  }: $TSFixMe) {
    this.id = id;
    this.name = name;
    this.description = description;
    this.timeCommitment = timeCommitment;
    this.learningObjectives = learningObjectives;

    // Keeping elements in the model for backward compatibility
    this.elements = elements;
    this.context = context || {};

    const { changeScope } = this.context;
    this.isUpdated =
      !changeScope ||
      changeScope.some((scope: $TSFixMe) => {
        return this.checkIfUpdated(scope);
      });

    this.lessons = elements.map((lesson: $TSFixMe) => {
      return new Lesson(
        Object.assign({ moduleId: id, context: this.context, drafts, authoringAtoms, authoringAtomRelations }, lesson)
      );
    });
  }

  get items() {
    return _(this.lessons)
      .chain()
      .map((lesson) => {
        return lesson.items;
      })
      .flatten()
      .value();
  }

  get gradableItems() {
    return _(this.items).filter((item) => item.isGraded);
  }

  get hasGradedItem() {
    return this.items.some(({ isGraded }) => isGraded);
  }

  get canModifyAndDelete() {
    return canModifyAndDeleteModule(this, this.context.isBranchLaunched);
  }

  get humanizedTime() {
    return humanizeTime(this.timeCommitment);
  }

  get coreLessons() {
    return this.lessons.filter((lesson) => lesson.trackId === TrackIds.CORE);
  }

  get honorLessons() {
    return this.lessons.filter((lesson) => lesson.trackId === TrackIds.HONORS);
  }

  get areValidLessons() {
    return areValidLessons(this.lessons);
  }

  checkIfUpdated(scope: $TSFixMe) {
    const { typeName, definition } = scope;

    switch (typeName) {
      case ChangeScopeTypes.CourseMaterial.COURSE_ELEMENT_LISTING:
      case ChangeScopeTypes.Item.LOCKS:
        // TODO: Kelvin/Omid: retrieve the appropriate object from the definition
        // once the BE adds more information to the changeScope of type "lock"
        return true;

      case ChangeScopeTypes.Module.MODULE:
        return definition.moduleId === this.id;

      // The parent module needs to be updated as all of these change scopes affect child components
      // Such as lessons/items etc.
      case ChangeScopeTypes.Module.MODULE_ELEMENT_LISTING:
      case ChangeScopeTypes.Lesson.LESSON:
      case ChangeScopeTypes.Lesson.LESSON_ELEMENT_LISTING:
      case ChangeScopeTypes.Item.ITEM:
        return definition.parentModuleId === this.id;

      default:
        return false;
    }
  }

  getParentIdsForItems(itemIds: Array<string>) {
    return this.lessons.reduce((prevParentIds, lesson) => {
      return Object.assign(prevParentIds, lesson.getParentIdsForItems(itemIds));
    }, {} as ParentIdsForItemId);
  }

  updateDrafts(drafts: $TSFixMe, draftsWithParentIds: $TSFixMe) {
    const draftsInModule = _(drafts).pick((draftName, draftId) => {
      const draftWithParent = draftsWithParentIds[draftId];
      return draftWithParent && draftWithParent.parentModuleId === this.id;
    });

    if (_(draftsInModule).isEmpty()) {
      return;
    }

    this.lessons.forEach((lesson) => {
      lesson.updateDrafts(draftsInModule, draftsWithParentIds);
    });
    this.isUpdated = true;
  }
}

export default Module;
